import React, { createContext, useContext } from 'react';
import ApiClient from '../../lib/api-client';
import { IAccountCreationDTO } from '@common-frontend-submodule/interfaces/account/iaccount-creation.interface';
import { IAccountUpdateDTO } from '@common-frontend-submodule/interfaces/account/iaccount-update.interface';
import { IGetAccountsRequestDTO } from '@common-frontend-submodule/interfaces/account/iget-accounts-request.interface';
import { IAccountDTO } from '@common-frontend-submodule/interfaces/account/iaccount.interface';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { AccountRoutes } from '@common-frontend-submodule/enums/routes/account-routes.enum';

const apiClient = ApiClient.getInstance();

interface AccountContextProps {
  getById: (accountId: string) => Promise<IApiResponseGenericDTO<IAccountDTO>>;
  allClientAccounts: (id: string) => Promise<IApiResponseGenericDTO<IAccountDTO[]>>;
  filter: (model: IGetAccountsRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IAccountDTO>>>;
  create: (model: IAccountCreationDTO) => Promise<IApiResponseGenericDTO<IAccountDTO>>;
  update: (accountId: string, model: IAccountUpdateDTO) => Promise<IApiResponseGenericDTO<IAccountDTO>>;
  delete: (accountId: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const AccountContext = createContext<AccountContextProps | undefined>(undefined);

export const AccountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const getById = async (accountId: string) => {
    return await apiClient.getRequest<IAccountDTO>(`${AccountRoutes.BasePrefix}/${accountId}`);
  };

  const allClientAccounts = async (id: string) => {
    return await apiClient.getRequest<IAccountDTO[]>(`${AccountRoutes.BasePrefix}/all/${id}`);
  };

  const filter = async (model: IGetAccountsRequestDTO) => {
    return await apiClient.postRequest<IGetAccountsRequestDTO, IPaginatedResultDTO<IAccountDTO>>(`${AccountRoutes.BasePrefix}/${AccountRoutes.List}`, model);
  };

  const create = async (model: IAccountCreationDTO) => {
    return await apiClient.postRequest<IAccountCreationDTO, IAccountDTO>(AccountRoutes.BasePrefix, model);
  };

  const update = async (accountId: string, model: IAccountUpdateDTO) => {
    return await apiClient.putRequest<IAccountUpdateDTO, IAccountDTO>(`${AccountRoutes.BasePrefix}/${accountId}`, model);
  };

  const deleteAccount = async (accountId: string) => {
    return await apiClient.deleteRequest<null, boolean>(`${AccountRoutes.BasePrefix}/${accountId}`);
  };


  return (
    <AccountContext.Provider value={{ getById, allClientAccounts, filter, create, update, delete: deleteAccount }}>
      {children}
    </AccountContext.Provider>
  );
};

export const useAccountStore = () => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccountStore must be used within an AccountProvider');
  }
  return context;
};
